import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent, type KeyboardEvent, type ReactNode } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router";
import { useNative } from "../../../shared/phone/media";
import { PhoneNav } from "../../../shared/phone/PhoneNav";
import { Sheet as PnSheet } from "../../../shared/phone/Sheet";
import type { Block } from "../../../shared/goals/messages";
import { Icon } from "../../components/ui/Icon";
import { Orb } from "../../components/ui/Orb";
import { Sheet } from "../../components/ui/Sheet";
import { ccApi, type ActionView, type ChatMessage, type ExplainOf, type Need, type ReadyLite, type ReadyStep, type Results, type Version } from "../../lib/ccApi";
import type { GoalView } from "../../lib/goalsApi";
import { runActive } from "../../lib/productsApi";
import { buildLog } from "../goal/runEvents";
import { useRun } from "../goal/useRun";
import { Blocks, type BlockCtx } from "./Blocks";
import { SettingsCard } from "./Cards";
import { AskBox } from "./Layout";
import { addLocal, localItems, onLocal, updateLocal, type Local } from "./local";
import { NeedSheet } from "./NeedSheet";
import { NeedsList } from "./Needs";
import { ReadyRows } from "./ReadySteps";
import { adsDay, dayLabel, fmt, longDay, parseTime, perResult, reviewWhen, sameDay, shortDay, shortGoal, totalSpent, usd, usd0 } from "./text";
import { errText, useCcToast, useHome } from "./useCc";
import { useDockInView } from "./V2Parts";
import { useCan } from "../../context/AuthContext";

/** The goal's chat docked beside its page's numbers (Command Center V2): shown or not, and what the page asks it. */
interface Dock {
  open: boolean;
  onClose: () => void;
  /** A question the page sends, such as Explain on an ad; a new `key` sends it once. */
  ask: { text: string; of?: ExplainOf; key: number } | null;
  /** The page's dates in words, for the chat's header. */
  dates: string;
  /** What needs the owner, as the chat reads it, for the page's own list. */
  onNeeds: (needs: Need[]) => void;
  /** A new value opens the wrap-up. */
  wrapKey: number;
}

interface Props {
  view: GoalView;
  reload: () => Promise<void>;
  dock: Dock;
}

/** What a page can ask of the chat: a question to send once (a new `key` sends it), and a new `wrapKey` opens the wrap-up. */
interface Asks {
  ask?: { text: string; of?: ExplainOf; key: number } | null;
  wrapKey?: number;
}

const textOf = (blocks: Block[]) =>
  blocks
    .map((b) => (b.type === "text" ? b.text : b.type === "list" ? b.items.join(". ") : b.type === "ask" ? b.questions.join(" ") : ""))
    .filter(Boolean)
    .join(" ")
    .replace(/\*\*/g, "");

/** The suggestions under a goal's chat, from where the goal stands (the prototype's ccSugList). */
function suggestions(state: string): string[] {
  if (state === "live") return ["How did the ads do last night?", "What did the ads bring lately?", "How is the money?", "How are the tests going?", "Show my settings", "Pause everything"];
  if (state === "paused") return ["Resume everything", "How did the ads do last night?", "How is the money?", "Show my settings"];
  if (state === "getting_ready") return ["What is left before launch?", "Show me the ad ideas", "What does the plan expect?"];
  if (state === "done") return ["What worked?", "What did it cost?"];
  return [];
}
const LOCAL_ASKS = new Set(["Show my settings", "Pause everything", "Resume everything"]);

// --- One message -------------------------------------------------------------------------------------

/** A message from a thread (before threads merged into the chat) carries that thread's title. */
function fromTag(m: ChatMessage) {
  return m.fromThreadTitle ? (
    <span className="cc-from">
      <Icon name="branch" />
      {m.fromThreadTitle}
    </span>
  ) : null;
}

function Ag({ children, when, mid }: { children: ReactNode; when?: string; mid?: string }) {
  const native = useNative();
  // Why: on a phone the agent's words run the full width, as in Messages; its mark sits on the review's own line.
  if (native)
    return (
      <div className="cc-msg ag" data-mid={mid}>
        <div className="cc-mb">
          {when && (
            <div className="cc-when">
              <Orb live={false} />
              {when}
            </div>
          )}
          {children}
        </div>
      </div>
    );
  return (
    <div className="cc-msg ag" data-mid={mid}>
      <Orb live={false} />
      <div className="cc-mb">
        {when && <div className="cc-when">{when}</div>}
        {children}
      </div>
    </div>
  );
}

/** The answer on its way: the run's lookups as it makes them, the prototype's "Looking it up". */
export function Pending({ runId }: { runId: string }) {
  const { run, events } = useRun(runId);
  const calls = buildLog(run, events)
    .filter((e) => e.tool && e.tool !== "answer")
    .map((e) => e.x);
  return (
    <div className="cc-msg ag cc-pending" data-run={runId}>
      <Orb live />
      <div className="cc-mb">
        {calls.length ? (
          <details className="cc-look" open>
            <summary>
              <span className="spin" />
              <span>Looking it up</span>
            </summary>
            <ul>
              {calls.slice(-6).map((x, i, all) => (
                <li key={i}>
                  {i === all.length - 1 && (!run || runActive(run.state)) ? (
                    <span className="spin" />
                  ) : (
                    <span className="cc-ck">
                      <Icon name="check" />
                    </span>
                  )}
                  <span>{x}</span>
                </li>
              ))}
            </ul>
          </details>
        ) : (
          <span className="typing">
            <i />
            <i />
            <i />
          </span>
        )}
      </div>
    </div>
  );
}

type Entry = { at: Date; nodes: ReactNode[] };

/** Feed entries with a divider wherever the day changes. */
function withDays(list: Entry[]): ReactNode[] {
  const out: ReactNode[] = [];
  let prev: Date | null = null;
  for (const e of list) {
    if (!prev || !sameDay(prev, e.at))
      out.push(
        <div className="cc-day" key={`d-${e.at.getTime()}`}>
          {dayLabel(e.at)}
        </div>,
      );
    prev = e.at;
    out.push(...e.nodes);
  }
  return out;
}

// --- The chat's data and actions ---------------------------------------------------------------------

/**
 * A goal's one chat: the reviews, what needs the owner, answers and changes, with everything it does. Threads
 * merged into it; a message from one shows that thread's title. The chat docked beside a goal's page on a
 * computer, a phone's goal page and the chat's own screen all read and act through this one hook.
 */
export function useGoalChat(view: GoalView, reload: () => Promise<void>, page: Asks = {}) {
  const g = view.goal;
  const goalId = g.id;
  const goalPath = `/goals/${encodeURIComponent(goalId)}`;
  const navigate = useNavigate();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const { home, refresh: refreshHome } = useHome();
  const { toast, say } = useCcToast();
  const can = useCan();
  // Why: asking writes to the goal's chat, an Editor's; resuming is an Admin's.
  const mayAsk = can("editor");
  const [main, setMain] = useState<ChatMessage[] | null>(null);
  const [needs, setNeeds] = useState<Need[]>([]);
  const [results, setResults] = useState<Results | null>(null);
  const [ready, setReady] = useState<ReadyLite | null>(null);
  const [steps, setSteps] = useState<ReadyStep[] | null>(null);
  const [versions, setVersions] = useState<Version[]>([]);
  const [pending, setPending] = useState<{ runId: string } | null>(null);
  const [panel, setPanel] = useState<{ focus: string | null } | null>(null);
  const [wrap, setWrap] = useState(false);
  const [asked, setAsked] = useState<Set<string>>(new Set());
  const [, setTick] = useState(0);
  const config = view.current?.config ?? null;

  useEffect(() => onLocal(() => setTick((t) => t + 1)), []);

  const homeGoal = useMemo(() => home?.products.flatMap((p) => p.goals).find((x) => x.id === goalId) ?? null, [home, goalId]);
  const product = useMemo(() => home?.products.find((p) => p.goals.some((x) => x.id === goalId)) ?? null, [home, goalId]);

  // --- Loading --------------------------------------------------------------------------------------

  const loadMain = useCallback(async () => {
    try {
      setMain((await ccApi.chat(goalId)).messages);
    } catch (e) {
      say(errText(e), { done: false });
    }
  }, [goalId, say]);
  const loadNeeds = useCallback(async () => {
    const r = await ccApi.needs(goalId).catch(() => null);
    if (r) setNeeds(r.needs);
  }, [goalId]);
  const withResults = g.state === "live" || g.state === "paused" || g.state === "done";
  const loadResults = useCallback(async () => {
    if (!withResults) return;
    setResults(await ccApi.results(goalId).catch(() => null));
  }, [goalId, withResults]);

  const gettingReady = g.state === "getting_ready";
  useEffect(() => {
    void loadMain();
    void loadNeeds();
    void loadResults();
    ccApi.ready(goalId).then(setReady, () => setReady(null));
    if (gettingReady) ccApi.readySteps(goalId).then((r) => setSteps(r.steps), () => setSteps([]));
    ccApi.versions(goalId).then((r) => setVersions(r.versions), () => setVersions([]));
  }, [goalId, loadMain, loadNeeds, loadResults, gettingReady]);

  // Why: new notices and answers land while you look; poll fast while an answer is on its way.
  useEffect(() => {
    const t = setInterval(
      () => {
        if (document.visibilityState !== "visible") return;
        void loadMain();
        void loadNeeds();
      },
      pending ? 3000 : 20000,
    );
    return () => clearInterval(t);
  }, [pending, loadMain, loadNeeds]);

  // --- The answer on its way ----------------------------------------------------------------------

  const { run: pendingRun } = useRun(pending?.runId ?? null);
  useEffect(() => {
    if (!pending || !pendingRun || runActive(pendingRun.state)) return;
    void (async () => {
      await loadMain();
      await loadNeeds();
      void refreshHome();
      if (pendingRun.state !== "done") addLocal(goalId, { kind: "error", text: pendingRun.error || "I could not answer this time. Ask again." });
      setPending(null);
    })();
  }, [pending, pendingRun, goalId, loadMain, loadNeeds, refreshHome]);

  // --- Acting -------------------------------------------------------------------------------------

  const pause = useCallback(
    async (on: boolean) => {
      try {
        if (on) await ccApi.pause(goalId);
        else await ccApi.resume(goalId);
        say(on ? "Everything is paused. The ads are off and nothing will send." : "Resumed. The ads are back on.");
        await Promise.all([reload(), loadMain()]);
        void refreshHome();
        return true;
      } catch (e) {
        say(errText(e), { done: false });
        return false;
      }
    },
    [goalId, reload, loadMain, refreshHome, say],
  );

  // --- Actions known by id: a review's proposals and the changes in answers, as they stand now ---------

  const [actions, setActions] = useState<Map<string, ActionView>>(new Map());
  const requested = useRef(new Set<string>());
  const fetchActions = useCallback(
    async (ids: string[]) => {
      for (let i = 0; i < ids.length; i += 100) {
        const r = await ccApi.actions(goalId, ids.slice(i, i + 100)).catch(() => null);
        if (r) setActions((m) => new Map([...m, ...r.actions.map((a) => [a.id, a] as const)]));
      }
    },
    [goalId],
  );
  const want = useCallback(
    (ids: string[]) => {
      const fresh = ids.filter((id) => !requested.current.has(id));
      if (!fresh.length) return;
      for (const id of fresh) requested.current.add(id);
      void fetchActions(fresh);
    },
    [fetchActions],
  );

  const decide = useCallback(
    async (n: Need, d: "approve" | "skip") => {
      try {
        const r = await ccApi.decide(n.id, d);
        requested.current.add(n.id);
        void fetchActions([n.id]);
        if (d === "approve" && (r.state === "refused" || r.state === "failed")) {
          const why = r.result && typeof r.result.error === "string" ? r.result.error : r.result && typeof r.result.message === "string" ? r.result.message : "";
          say(`It could not run.${why ? ` ${why}` : ""}`, { done: false });
        } else if (d === "approve" && r.state === "approved") say("Approved. It runs in a few minutes.");
        else say(d === "skip" ? "Skipped. Nothing happens." : "Done.");
        await Promise.all([loadNeeds(), loadMain()]);
        if (n.kind === "goal.change") await reload();
        void refreshHome();
        return true;
      } catch (e) {
        say(errText(e), { done: false });
        return false;
      }
    },
    [fetchActions, loadNeeds, loadMain, reload, refreshHome, say],
  );

  const send = async (text: string, of?: ExplainOf) => {
    if (pending) {
      say("Wait for the answer first.", { done: false });
      return;
    }
    setAsked((a) => new Set(a).add(text));
    if (LOCAL_ASKS.has(text)) {
      addLocal(goalId, { kind: "me", text });
      if (text === "Show my settings") addLocal(goalId, { kind: "settings" });
      else addLocal(goalId, { kind: "pause", on: text === "Pause everything", done: null });
      return;
    }
    try {
      const r = await ccApi.send(goalId, text, of);
      // Why: a read of the chat that lands while the question is on its way already holds it.
      setMain((m) => ((m ?? []).some((x) => x.id === r.message.id) ? m : [...(m ?? []), r.message]));
      setPending({ runId: r.runId });
    } catch (e) {
      say(errText(e), { done: false });
    }
  };
  const sendRef = useRef(send);
  sendRef.current = send;

  // --- Arriving with something to do -----------------------------------------------------------------

  const handled = useRef<string | null>(null);
  useEffect(() => {
    if (handled.current === location.key) return;
    handled.current = location.key;
    // Why: a phone's goal page sends its Explain here as the chat's screen opens, with the question it asks.
    const st = (location.state ?? {}) as { runId?: string; said?: string; ask?: { text: string; of?: ExplainOf } };
    if (st.runId) setPending({ runId: st.runId });
    if (st.said) say(st.said);
    if (st.ask) void sendRef.current(st.ask.text, st.ask.of);
    const need = params.get("need");
    if (need !== null) {
      setPanel({ focus: need || null });
      const next = new URLSearchParams(params);
      next.delete("need");
      setParams(next, { replace: true });
    }
    if (st.runId || st.said || st.ask) navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
  }, [location, params, setParams, navigate, say]);

  // --- What the feed shows -------------------------------------------------------------------------

  const msgs = main;
  const locals = localItems(goalId);

  const liveAt = useMemo(() => {
    const m = new Map<string, string>();
    for (const msg of msgs ?? [])
      for (const b of msg.body.blocks)
        if (b.type === "live") m.set(b.part === "results" ? "money" : b.part === "emails" ? "ads" : b.part, msg.id);
    for (const l of locals) if (l.item.kind === "settings") m.set("settings", l.id);
    return m;
  }, [msgs, locals]);

  // Why: a change in an answer shows Apply or what became of it, so its action is read as it stands.
  const diffIds = useMemo(() => (msgs ?? []).flatMap((m) => m.body.blocks.flatMap((b) => (b.type === "diff" && b.actionId ? [b.actionId] : []))).join(","), [msgs]);
  useEffect(() => {
    if (diffIds) want(diffIds.split(","));
  }, [diffIds, want]);

  const nextAt = reviewWhen(homeGoal?.nextReview ?? null)?.when ?? null;
  const ctx: BlockCtx = {
    goalId,
    config,
    plan: view.current?.plan ?? null,
    version: view.current?.version ?? g.version,
    state: g.state,
    needs,
    actions,
    want,
    results,
    ready,
    liveAt,
    nextReview: nextAt,
    spentCents: results ? totalSpent(results) : 0,
    openNeed: (id) => setPanel({ focus: id }),
    decide,
    say: (m, ok = true) => say(m, { done: ok }),
    reload: async () => {
      await reload();
      void loadMain();
    },
    pause: (on) => void pause(on),
  };

  const review = nextAt ? `The agent keeps working and reviews again ${nextAt}.` : "The agent keeps working.";

  const renderLocal = (l: Local): ReactNode => {
    const it = l.item;
    switch (it.kind) {
      case "me":
        return (
          <div className="cc-msg me" key={l.id}>
            <div className="cc-mw">
              <div className="cc-bub">{it.text}</div>
            </div>
          </div>
        );
      case "settings":
        return (
          <Ag key={l.id}>
            <p>This goal's settings. Only you change them, and the agent works inside them.</p>
            {config && (
              <div className={`cc-host${liveAt.get("settings") === l.id ? "" : " cc-still"}`} inert={liveAt.get("settings") !== l.id || undefined}>
                <SettingsCard
                  goalId={goalId}
                  config={config}
                  version={view.current?.version ?? g.version}
                  state={g.state}
                  spentCents={results ? totalSpent(results) : 0}
                  editHref={`${goalPath}/edit`}
                  say={(m, ok = true) => say(m, { done: ok })}
                  onSaved={ctx.reload}
                  onPause={(on) => void pause(on)}
                />
              </div>
            )}
          </Ag>
        );
      case "pause":
        return (
          <Ag key={l.id}>
            <p>
              {it.on
                ? "This turns the ads off and stops all emails and replies now. I keep reading results, and nothing is deleted."
                : "This turns the ads back on and lets emails and replies go out again."}
            </p>
            {it.done === null ? (
              <div className="m-act">
                <button
                  type="button"
                  className={it.on ? "btn sm warn" : "btn sm"}
                  onClick={() => void pause(it.on).then((ok) => ok && updateLocal(goalId, l.id, { ...it, done: "yes" }))}
                >
                  <Icon name={it.on ? "pause" : "play"} />
                  {it.on ? "Pause everything" : "Resume"}
                </button>
                <button type="button" className="btn sm ghost" onClick={() => updateLocal(goalId, l.id, { ...it, done: "no" })}>
                  {it.on ? "Keep it running" : "Stay paused"}
                </button>
              </div>
            ) : (
              <div className="m-act">
                <span className={`chip${it.done === "yes" && !it.on ? " ok" : ""}`}>{it.done === "no" ? "No change" : it.on ? "Paused" : "Resumed"}</span>
              </div>
            )}
          </Ag>
        );
      case "error":
        return (
          <Ag key={l.id}>
            <p>{it.text}</p>
          </Ag>
        );
      default:
        return null;
    }
  };

  const renderMsg = (m: ChatMessage): ReactNode => {
    if (m.role === "owner")
      return (
        <div className="cc-msg me" key={m.id} data-mid={m.id}>
          <div className="cc-mw">
            {fromTag(m)}
            <div className="cc-bub">{textOf(m.body.blocks)}</div>
          </div>
        </div>
      );
    // Why: a line about what the owner did (a change applied, a pause, an approval) reads as a note, not the agent speaking.
    if (m.role === "notice" && (m.kind === "change" && !m.actionId || m.fromThreadTitle))
      return (
        <div className="cc-note" key={m.id} data-mid={m.id}>
          <Icon name={m.fromThreadTitle ? "branch" : "check"} />
          <span>
            {m.fromThreadTitle && <b>{m.fromThreadTitle}: </b>}
            {textOf(m.body.blocks)}
          </span>
        </div>
      );
    return (
      <Ag key={m.id} when={m.body.when} mid={m.id}>
        {fromTag(m)}
        <Blocks blocks={m.body.blocks} mid={m.id} runId={m.runId} ctx={ctx} />
      </Ag>
    );
  };

  // Why: older days fold into one line, so the chat opens on its latest day and stays about your questions.
  const feed = (() => {
    if (!msgs) return <p className="cc-fine">Loading the chat…</p>;
    const entries: Entry[] = [
      ...msgs.map((m) => ({ at: parseTime(m.createdAt), nodes: [renderMsg(m)] })),
      ...locals.map((l) => ({ at: new Date(l.at), nodes: [renderLocal(l)] })),
    ].toSorted((a, b) => a.at.getTime() - b.at.getTime());
    const last = entries[entries.length - 1]?.at ?? new Date();
    const early = entries.filter((e) => !sameDay(e.at, last));
    const late = entries.filter((e) => sameDay(e.at, last));
    const set = versions.length ? parseTime(versions[versions.length - 1].created_at) : null;
    const updates = early.length;
    return (
      <>
        {early.length > 0 && (
          <details className="cc-earlier">
            <summary>
              <Icon name="chev" />
              <span>
                Earlier{" "}
                <em>
                  · {shortDay(early[0].at)} to {dayLabel(early[early.length - 1].at).toLowerCase() === "yesterday" ? "yesterday" : shortDay(early[early.length - 1].at)} · {updates}{" "}
                  {updates > 1 ? "updates" : "update"}
                </em>
              </span>
            </summary>
            <div className="cc-ein">
              <Ag>
                <p>
                  This chat holds everything about this goal{set ? ` since you set it on ${longDay(set)}` : ""}. Earlier changes are in the plan's versions and the full log.
                </p>
                <div className="cc-chips">
                  <button type="button" onClick={() => navigate(`${goalPath}/plan`)}>
                    The plan
                  </button>
                  <button type="button" onClick={() => navigate(`${goalPath}/log`)}>
                    The full log
                  </button>
                </div>
              </Ag>
              {withDays(early)}
            </div>
          </details>
        )}
        {withDays(late)}
        {g.state === "getting_ready" && steps && (
          <Ag when="Getting ready">
            <p>
              The plan is approved{view.current && view.current.version > 1 ? `, version ${view.current.version}` : ""}. Before the ads go live
              {homeGoal && adsDay(homeGoal) ? (
                <>
                  {" "}
                  on <b>{adsDay(homeGoal)}</b>
                </>
              ) : null}
              , here is what is left:
            </p>
            <ReadyRows goalPath={goalPath} steps={steps} ready={ready} />
          </Ag>
        )}
        {!entries.length && g.state !== "getting_ready" && (
          <Ag>
            <p>This chat holds everything about this goal: the morning reviews, what needs you, and your questions. Ask anything below.</p>
          </Ag>
        )}
      </>
    );
  })();

  /** Changes whenever a message (yours, an answer, a notice) arrives, so the chat can move to its end. */
  const count = (msgs?.length ?? 0) + locals.length + (pending ? 1 : 0);

  // The page's questions (Explain) go through the same send, once each.
  const asks = useRef<number | null>(null);
  const ask = page.ask ?? null;
  useEffect(() => {
    if (!ask || asks.current === ask.key) return;
    asks.current = ask.key;
    void sendRef.current(ask.text, ask.of);
  }, [ask]);
  const wrapKey = page.wrapKey ?? 0;
  useEffect(() => {
    if (wrapKey) setWrap(true);
  }, [wrapKey]);

  const sugs = mayAsk ? suggestions(g.state).filter((s) => !asked.has(s) && (s !== "Resume everything" || can("admin"))).slice(0, 4) : [];
  const wrapMsg = (main ?? []).filter((m) => m.kind === "wrapup").at(-1) ?? null;

  /** The wrap-up's numbers and the agent's words: in a sheet on a computer, in the kit's sheet on a phone. */
  const wrapBody = (
    <>
      {config && (
        <div className="wnum">
          <div>
            <b>{fmt(results?.totals.results ?? 0)}</b>
            <span>
              {config.result.label}, goal {fmt(config.result.target)}
            </span>
          </div>
          <div>
            <b>{usd0(results ? totalSpent(results) : 0)}</b>
            <span>of {usd(config.money.budgetCents)} spent</span>
          </div>
          <div>
            <b>{perResult(results ? totalSpent(results) : 0, results?.totals.results ?? 0)}</b>
            <span>per result</span>
          </div>
        </div>
      )}
      <div className="cc-mb">{wrapMsg ? <Blocks blocks={wrapMsg.body.blocks} mid={wrapMsg.id} runId={wrapMsg.runId} ctx={ctx} /> : <p>The agent writes the wrap-up when the goal ends.</p>}</div>
    </>
  );
  const wrapTitle = `${shortGoal(g.sentence)}${config ? ((results?.totals.results ?? 0) >= config.result.target ? ": reached" : ": ended") : ""}`;

  return { g, goalId, goalPath, config, product, needs, ready, results, panel, setPanel, wrap, setWrap, pending, decide, pause, send, feed, loaded: msgs !== null, count, sugs, mayAsk, review, wrapBody, wrapTitle, toast, say };
}

// --- The chat docked on a computer ---------------------------------------------------------------------

/**
 * A goal's one chat, docked beside its page (Command Center V2): the reviews, what needs the owner, answers and
 * changes.
 */
export function GoalChat({ view, reload, dock: docked }: Props) {
  const c = useGoalChat(view, reload, { ask: docked.ask, wrapKey: docked.wrapKey });
  const { g, goalId, config, product, needs, ready, results, panel, setPanel, wrap, setWrap, pending, decide, send, feed, loaded, count, sugs, mayAsk, review, wrapBody, toast } = c;
  const onNeeds = docked.onNeeds;
  useEffect(() => {
    onNeeds(needs);
  }, [needs, onNeeds]);

  // Why: a new message (yours, an answer, a notice) brings the chat to its end, as the prototype's ccScroll does.
  const seen = useRef<number | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!loaded) return;
    const prev = seen.current;
    seen.current = count;
    if (prev !== null && count > prev)
      requestAnimationFrame(() => {
        if (panelRef.current) panelRef.current.scrollTop = panelRef.current.scrollHeight;
      });
  }, [count, loaded]);
  useEffect(() => {
    if (docked.open && panelRef.current) panelRef.current.scrollTop = panelRef.current.scrollHeight;
  }, [docked.open]);
  const dockRef = useRef<HTMLElement>(null);
  useDockInView(dockRef, docked.open);

  const dock = (
    <>
      {sugs.length > 0 && (
        <div className="cc-sugs">
          {sugs.map((s) => (
            <button type="button" key={s} onClick={() => void send(s)}>
              {s}
            </button>
          ))}
        </div>
      )}
      {mayAsk && <AskBox placeholder="Ask about this goal, or change it" busy={!!pending} onSend={(t) => void send(t)} />}
    </>
  );

  const sheets = (
    <>
      <Sheet open={!!panel} onClose={() => setPanel(null)} label="Needs you" className="wide">
        <NeedsList goalId={goalId} needs={needs} config={config} ready={ready} focus={panel?.focus ?? null} review={review} onDecide={decide} />
      </Sheet>
      <Sheet open={wrap} onClose={() => setWrap(false)} label="Wrap-up">
        <div className="eyebrow">Wrap-up{product ? ` · ${product.name}` : ""}</div>
        <h2 style={{ marginTop: 6 }}>
          {shortGoal(g.sentence)}
          {config ? ((results?.totals.results ?? 0) >= config.result.target ? ": reached" : ": ended") : ""}
        </h2>
        {g.endedAt && (
          <p className="lede" style={{ fontSize: "var(--fs-text)" }}>
            Written by the agent when the goal ended, {longDay(parseTime(g.endedAt))}.
          </p>
        )}
        {wrapBody}
      </Sheet>
      {toast}
    </>
  );

  return (
      <>
        <aside ref={dockRef} className={`gchat${docked.open ? " on" : ""}`} aria-label="Ask about this goal" hidden={!docked.open}>
          <header className="gchat-h">
            <span className="ki">
              <Icon name="spark" />
            </span>
            <div>
              <b>Ask about this goal</b>
              <span>{docked.dates}, from Meta's numbers</span>
            </div>
            <button type="button" className="fx" onClick={docked.onClose} aria-label="Close">
              <Icon name="x" />
            </button>
          </header>
          <div className="gchat-b cc-feed" ref={panelRef}>
            {feed}
            {pending && <Pending runId={pending.runId} />}
          </div>
          <div className="gchat-f">{dock}</div>
        </aside>
        {sheets}
      </>
    );
}

// --- The chat's own screen on a phone and an iPad -----------------------------------------------------

const LINES = 5;

/** The box on the keyboard: grows to five lines as you type, Enter sends and Shift and Enter starts a new line. */
function ChatBox({ busy, onSend, onTyping }: { busy: boolean; onSend: (text: string) => void; onTyping: (typing: boolean) => void }) {
  const [text, setText] = useState("");
  const ref = useRef<HTMLTextAreaElement>(null);
  const grow = (t: HTMLTextAreaElement) => {
    t.style.height = "auto";
    const line = parseFloat(getComputedStyle(t).lineHeight) || 22;
    t.style.height = `${Math.min(t.scrollHeight, line * LINES + 20)}px`;
  };
  const send = (e?: FormEvent) => {
    e?.preventDefault();
    const v = text.trim();
    if (!v) {
      ref.current?.focus();
      return;
    }
    if (busy) return;
    setText("");
    onTyping(false);
    if (ref.current) {
      ref.current.value = "";
      grow(ref.current);
    }
    onSend(v);
  };
  const key = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };
  return (
    <form className="gp-box" onSubmit={send}>
      <textarea
        ref={ref}
        rows={1}
        placeholder="Ask about this goal"
        aria-label="Ask about this goal"
        enterKeyHint="send"
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          onTyping(e.target.value.trim().length > 0);
          grow(e.target);
        }}
        onKeyDown={key}
      />
      <button className="gp-send" type="submit" aria-label="Send" disabled={busy}>
        <Icon name="send" />
      </button>
    </form>
  );
}

/**
 * A goal's chat as its own screen on a phone and an iPad, like Messages: the messages scroll above, the box sits
 * on the keyboard, and the suggestions hide while you type. What needs you opens one at a time in a sheet.
 */
export function GoalChatScreen({ view, reload }: { view: GoalView; reload: () => Promise<void> }) {
  const c = useGoalChat(view, reload);
  const { g, goalId, goalPath, config, needs, ready, panel, setPanel, wrap, setWrap, pending, decide, send, feed, loaded, count, sugs, mayAsk, review, wrapBody, wrapTitle, toast } = c;
  const [typing, setTyping] = useState(false);
  const screen = useRef<HTMLDivElement>(null);

  // Why: the chat opens on its newest message, and each new one (yours, an answer, a notice) brings it to the end.
  const end = useCallback((smooth: boolean) => {
    const el = screen.current?.closest<HTMLElement>(".pn-pane, .pn-main");
    el?.scrollTo({ top: el.scrollHeight, behavior: smooth ? "smooth" : "auto" });
  }, []);
  const seen = useRef<number | null>(null);
  useEffect(() => {
    if (!loaded) return;
    const prev = seen.current;
    seen.current = count;
    requestAnimationFrame(() => end(prev !== null && count > prev));
  }, [count, loaded, end]);

  return (
    <div className="pn-screen gp-chat" ref={screen}>
      <PhoneNav
        title={
          <span className="gp-ct">
            <b>Ask the agent</b>
            <span>{shortGoal(g.sentence)}</span>
          </span>
        }
        back={{ to: goalPath, label: "Goal" }}
      />
      <div className="ga gw ccx gp-feed">
        <div className="cc-feed">
          {feed}
          {pending && <Pending runId={pending.runId} />}
        </div>
      </div>
      <div className="pn-foot gp-cf">
        {sugs.length > 0 && !typing && (
          <div className="pn-chips gp-sugs">
            {sugs.map((s) => (
              <button type="button" className="pn-chip" key={s} onClick={() => void send(s)}>
                {s}
              </button>
            ))}
          </div>
        )}
        {mayAsk && <ChatBox busy={!!pending} onSend={(t) => void send(t)} onTyping={setTyping} />}
      </div>
      <NeedSheet goalId={goalId} needs={needs} config={config} ready={ready} focus={panel ? (panel.focus ?? needs[0]?.id ?? null) : null} review={review} onDecide={decide} onClose={() => setPanel(null)} />
      <PnSheet open={wrap} onClose={() => setWrap(false)} title="Wrap-up">
        <div className="ga gw ccx gp-in">
          <h2 className="gp-in-h">{wrapTitle}</h2>
          {g.endedAt && <p className="gp-in-s">Written by the agent when the goal ended, {longDay(parseTime(g.endedAt))}.</p>}
          {wrapBody}
        </div>
      </PnSheet>
      {toast}
    </div>
  );
}
